// Runs the idle auto-end (src/idle.ts) on a clock: after three quiet minutes the Director signs off with one line
// and the app ends the call. The carryover is saved first, so one tap reconnects right where things were.
import { api } from "./api";
import { condenseTranscript } from "./chat";
import { director } from "./director";
import { IDLE_WARNING, idleStep } from "./idle";
import { isActive } from "./playback";
import { get, markActivity, say, set } from "./store";

let timer = 0;
let warnedAt: number | null = null;
let spokeSinceWarn = false;
let lastBusy = 0;
let lastStatus = "";

const live = () => {
  const s = get().director;
  return s === "listening" || s === "thinking" || s === "speaking";
};
const busy = () => isActive(get().playback) || Object.values(get().jobs).some((j) => j.status === "queued" || j.status === "running");

function tick() {
  const s = get();
  const now = Date.now();
  if (busy()) lastBusy = now;
  if (warnedAt !== null && s.director === "speaking") spokeSinceWarn = true;
  lastStatus = s.director;
  const action = idleStep({ now, connected: live(), lastActivity: s.lastActivity, busy: busy(), lastBusy, warnedAt, directorSpeaking: s.director === "speaking", spokeSinceWarn });
  if (action === "warn") {
    warnedAt = now;
    spokeSinceWarn = false;
    director.context(IDLE_WARNING);
  } else if (action === "cancel") {
    warnedAt = null;
    spokeSinceWarn = false;
  } else if (action === "end") {
    warnedAt = null;
    spokeSinceWarn = false;
    void end();
  }
}

async function end() {
  const s = get();
  await api.saveCarryover(condenseTranscript(s.transcript, { userName: s.me?.user.name.split(" ")[0] })).catch(() => undefined);
  director.stop("idle");
  set({ directorEnded: "idle", micMuted: false });
  say("studio", "The call ended after three quiet minutes. Tap your Director to pick up where you left off.");
}

const onInput = () => markActivity();

/** Start watching (signed in). Returns a stop function. */
export function startIdleWatch(): () => void {
  stopIdleWatch();
  window.addEventListener("pointerdown", onInput, { passive: true });
  window.addEventListener("keydown", onInput);
  timer = window.setInterval(tick, 1000);
  return stopIdleWatch;
}

export function stopIdleWatch() {
  window.clearInterval(timer);
  timer = 0;
  warnedAt = null;
  window.removeEventListener("pointerdown", onInput);
  window.removeEventListener("keydown", onInput);
}

/** For the status line: whether the app has asked the Director to sign off. */
export const signingOff = () => warnedAt !== null && lastStatus !== "off";
